import { m } from "framer-motion";
import { ArrowLeft, Compass, Home, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

/**
 * Catch-all for a URL that matches no route.
 *
 * Distinct from the neighbouring full-page states: NotAuthorized is "this
 * exists, you can't see it", ModuleDisabled is "your organisation hasn't
 * enabled this", and ErrorFallback is "something threw". This one is simply
 * "there is nothing at this address" — so it names the address that failed
 * and offers a way onward, rather than apologising.
 */
export function NotFoundPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const suggestions = [
    {
      to: "/contracts",
      icon: Search,
      label: t("notFound.linkContracts", { defaultValue: "All Contracts" }),
      hint: t("notFound.linkContractsHint", {
        defaultValue: "Browse and search every contract",
      }),
    },
    {
      to: "/dashboard",
      icon: Compass,
      label: t("notFound.linkDashboard", { defaultValue: "Dashboard" }),
      hint: t("notFound.linkDashboardHint", {
        defaultValue: "Your activity and what needs attention",
      }),
    },
  ];

  return (
    <m.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="
        flex flex-col
        items-center
        justify-center
        min-h-[400px]
        p-8
        text-center
        bg-[rgb(var(--card))]
        border border-[rgb(var(--border))]
        rounded-2xl
        shadow-lifted
      "
    >
      <div className="relative mb-6">
        <div
          className="
            absolute inset-0
            bg-[rgb(var(--accent-fill))]
            opacity-10
            blur-xl
            rounded-full
            animate-pulse
          "
        />

        <div
          className="
            relative
            p-5
            bg-[rgb(var(--card-muted))]
            rounded-full
            border border-[rgb(var(--border))]
            text-[rgb(var(--text-accent))]
          "
        >
          <Compass size={48} />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-[rgb(var(--text-primary))] mb-2">
        {t("notFound.title", { defaultValue: "This page doesn't exist" })}
      </h2>

      <p className="text-[rgb(var(--text-muted))] max-w-md leading-relaxed">
        {t("notFound.description", {
          defaultValue:
            "The link may be out of date, or the item may have been renamed or removed.",
        })}
      </p>

      {/* The address that failed — without it the reader can't tell a typo
          from a stale bookmark, and can't say what broke when reporting it. */}
      <code
        className="
          mt-4 max-w-full
          px-3 py-1.5
          rounded-md
          bg-[rgb(var(--card-muted))]
          border border-[rgb(var(--border))]
          text-xs
          text-[rgb(var(--text-muted))]
          break-all
        "
      >
        {pathname}
      </code>

      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="
            inline-flex items-center gap-2
            px-4 py-2 rounded-lg
            border border-[rgb(var(--border))]
            text-sm font-medium
            text-[rgb(var(--text-primary))]
            hover:bg-[rgb(var(--card-muted))]
            transition
          "
        >
          <ArrowLeft size={16} />
          {t("notFound.back", { defaultValue: "Go back" })}
        </button>

        <Link
          to="/"
          className="
            inline-flex items-center gap-2
            px-4 py-2 rounded-lg
            bg-[rgb(var(--accent-fill))]
            text-sm font-semibold text-[rgb(var(--accent-ink))]
            hover:opacity-90
            transition
          "
        >
          <Home size={16} />
          {t("notFound.home", { defaultValue: "Go home" })}
        </Link>
      </div>

      {/* Somewhere to go next, rather than a dead end with only "back". */}
      <div className="mt-8 w-full max-w-lg grid grid-cols-1 sm:grid-cols-2 gap-3">
        {suggestions.map(({ to, icon: Icon, label, hint }) => (
          <Link
            key={to}
            to={to}
            className="
              group flex items-start gap-3
              p-3 rounded-xl text-left
              border border-[rgb(var(--border))]
              bg-[rgb(var(--card-muted))]
              hover:border-[rgb(var(--text-accent))]
              transition
            "
          >
            <span
              className="
                shrink-0 mt-0.5 p-2 rounded-lg
                bg-[rgb(var(--card))]
                text-[rgb(var(--text-accent))]
                border border-[rgb(var(--border))]
              "
            >
              <Icon size={16} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-[rgb(var(--text-primary))]">
                {label}
              </span>
              <span className="block text-xs text-[rgb(var(--text-muted))]">
                {hint}
              </span>
            </span>
          </Link>
        ))}
      </div>
    </m.div>
  );
}
